import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  HeartIcon,
  EyeIcon,
  TagIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { blogService, BlogPost } from '../../services/blogService';

export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [tags, setTags] = useState<{ tag: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadData();
  }, [currentPage, selectedCategory, selectedTag, searchQuery]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [postsData, featured, categoriesData, tagsData] = await Promise.all([
        blogService.getBlogPosts(currentPage, 12, {
          category: selectedCategory || undefined,
          tag: selectedTag || undefined,
          search: searchQuery || undefined,
        }),
        currentPage === 1 && !selectedCategory && !selectedTag && !searchQuery
          ? blogService.getFeaturedPosts(3)
          : Promise.resolve([]),
        blogService.getCategories(),
        blogService.getTags(20),
      ]);

      setPosts(postsData.posts);
      setTotalPages(postsData.totalPages);
      setFeaturedPosts(featured);
      setCategories(categoriesData);
      setTags(tagsData);
    } catch (error) {
      console.error('Error loading blog data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    updateSearchParams({ search: searchQuery, page: '1' });
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    updateSearchParams({ category, page: '1' });
  };

  const handleTagClick = (tag: string) => {
    setSelectedTag(tag);
    setCurrentPage(1);
    updateSearchParams({ tag, page: '1' });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateSearchParams({ page: page.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedTag('');
    setSearchQuery('');
    setCurrentPage(1);
    setSearchParams({});
  };

  const updateSearchParams = (params: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return 'Unknown date';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const truncateContent = (content: string, maxLength: number = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Blog & News</h1>
          <p className="text-xl opacity-90 max-w-2xl">
            Discover stories, updates, and insights from our community of creators
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mt-8 max-w-2xl">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-12 pr-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
              />
            </div>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Filters Header */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <FunnelIcon className="h-5 w-5 mr-2" />
                  Filters
                </h3>
                {(selectedCategory || selectedTag || searchQuery) && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Active Filters */}
              {(selectedCategory || selectedTag || searchQuery) && (
                <div className="space-y-2 mb-4 pb-4 border-b">
                  {searchQuery && (
                    <div className="flex items-center text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded">
                      Search: {searchQuery}
                    </div>
                  )}
                  {selectedCategory && (
                    <div className="flex items-center text-sm bg-purple-50 text-purple-700 px-3 py-1 rounded">
                      Category: {selectedCategory}
                    </div>
                  )}
                  {selectedTag && (
                    <div className="flex items-center text-sm bg-green-50 text-green-700 px-3 py-1 rounded">
                      Tag: {selectedTag}
                    </div>
                  )}
                </div>
              )}

              {/* Categories */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Categories</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`block w-full text-left px-3 py-2 rounded ${
                      !selectedCategory
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    All Categories
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryChange(category)}
                      className={`block w-full text-left px-3 py-2 rounded ${
                        selectedCategory === category
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Popular Tags */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Popular Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {tags.map(({ tag, count }) => (
                    <button
                      key={tag}
                      onClick={() => handleTagClick(tag)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                        selectedTag === tag
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <TagIcon className="h-3 w-3 mr-1" />
                      {tag} ({count})
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-600">Loading posts...</p>
              </div>
            ) : (
              <>
                {/* Featured Posts */}
                {featuredPosts.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Featured</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {featuredPosts.map((post) => (
                        <Link
                          key={post.id}
                          to={`/blog/${post.id}`}
                          className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                        >
                          {post.mediaUrls[0] && (
                            <img
                              src={post.mediaUrls[0]}
                              alt={post.title}
                              className="w-full h-48 object-cover"
                            />
                          )}
                          <div className="p-4">
                            {post.category && (
                              <span className="text-xs font-semibold text-blue-600 uppercase">
                                {post.category}
                              </span>
                            )}
                            <h3 className="font-bold text-lg mt-1 line-clamp-2">{post.title}</h3>
                            <p className="text-gray-600 text-sm mt-2 line-clamp-2">
                              {truncateContent(post.content, 100)}
                            </p>
                            <div className="flex items-center mt-4 text-sm text-gray-500">
                              <img
                                src={post.creatorAvatar || '/default-avatar.png'}
                                alt={post.creatorName}
                                className="h-6 w-6 rounded-full mr-2"
                              />
                              <span>{post.creatorName}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Blog Posts Grid */}
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {selectedCategory || selectedTag || searchQuery ? 'Results' : 'Latest Posts'}
                </h2>

                {posts.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-8 text-center">
                    <p className="text-gray-600">No posts found matching your criteria.</p>
                    <button
                      onClick={clearFilters}
                      className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {posts.map((post) => (
                        <Link
                          key={post.id}
                          to={`/blog/${post.id}`}
                          className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden"
                        >
                          {post.mediaUrls[0] && (
                            <img
                              src={post.mediaUrls[0]}
                              alt={post.title}
                              className="w-full h-48 object-cover"
                            />
                          )}
                          <div className="p-6">
                            {post.category && (
                              <span className="text-xs font-semibold text-blue-600 uppercase">
                                {post.category}
                              </span>
                            )}
                            <h3 className="font-bold text-xl mt-2 line-clamp-2">{post.title}</h3>
                            <p className="text-gray-600 mt-2 line-clamp-3">
                              {truncateContent(post.content)}
                            </p>

                            {/* Post Meta */}
                            <div className="mt-4 flex items-center justify-between">
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span className="flex items-center">
                                  <EyeIcon className="h-4 w-4 mr-1" />
                                  {post.viewCount}
                                </span>
                                <span className="flex items-center">
                                  <HeartIcon className="h-4 w-4 mr-1" />
                                  {post.likeCount}
                                </span>
                                <span className="flex items-center">
                                  <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
                                  {post.commentCount}
                                </span>
                              </div>
                              <span className="flex items-center text-sm text-gray-500">
                                <ClockIcon className="h-4 w-4 mr-1" />
                                {formatDate(post.publishedAt)}
                              </span>
                            </div>

                            {/* Author */}
                            <div className="mt-4 pt-4 border-t flex items-center">
                              <img
                                src={post.creatorAvatar || '/default-avatar.png'}
                                alt={post.creatorName}
                                className="h-8 w-8 rounded-full mr-3"
                              />
                              <span className="text-sm font-medium text-gray-700">
                                {post.creatorName}
                              </span>
                            </div>

                            {/* Tags */}
                            {post.tags.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {post.tags.slice(0, 3).map((tag) => (
                                  <span
                                    key={tag}
                                    className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                                  >
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="mt-8 flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                          // Show first, last, current, and adjacent pages
                          if (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`px-4 py-2 rounded-lg ${
                                  currentPage === page
                                    ? 'bg-blue-600 text-white'
                                    : 'border border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {page}
                              </button>
                            );
                          } else if (page === currentPage - 2 || page === currentPage + 2) {
                            return (
                              <span key={page} className="px-2">
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
